import React, { useState } from 'react';
import { reservationsApi, maintenanceApi, hotelsApi } from '../../services/api';
import { fmt } from '../../utils/billing';
import { downloadGstExcel, downloadGstPdf, hotelInfoFrom, toDDMMYYYY } from '../../utils/gstExport';
import { useNotify } from '../../context/ConfirmContext';

export default function SuperAdminTools({ hotel, onSuccess }) {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [dateMode, setDateMode] = useState('month'); // 'month' or 'range'
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [downloadFormat, setDownloadFormat] = useState('excel'); // 'excel' or 'pdf'
  
  const [records, setRecords] = useState([]);
  const [hotelMap, setHotelMap] = useState({});
  const [allHotelsRaw, setAllHotelsRaw] = useState([]);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState(null);

  const checkCount = async () => {
    setLoading(true);
    try {
      const monthParam = dateMode === 'month' ? selectedMonth : null;
      const startParam = dateMode === 'range' ? startDate : null;
      const endParam = dateMode === 'range' ? endDate : null;

      const [all, allHotels] = await Promise.all([
        reservationsApi.list(hotel?.id || '', 'checked-out', null, monthParam, startParam, endParam),
        hotelsApi.list()
      ]);
      
      const hMap = {};
      allHotels.forEach(sh => { 
        hMap[sh.id] = { 
          name: sh.gst_name || sh.name,
          displayName: sh.name,
          gst: sh.gst_number || 'No GST',
          address: [sh.address, sh.city].filter(Boolean).join(', '),
          city: sh.city || '',
          gstName: sh.gst_name || '',
          phone: sh.phone || 'N/A'
        }; 
      });
      setHotelMap(hMap);
      setAllHotelsRaw(allHotels);
      setRecords((all || []).filter(r => r.status === 'checked-out'));
    } catch (err) {
      console.error('Scan Error:', err);
      onSuccess?.('Error scanning records: ' + err.message);
    }
    setLoading(false);
  };

  const nonGstRecords = records.filter(r => !r.billing_type || r.billing_type === 'non-gst');
  const gstRecords = records.filter(r => r.billing_type === 'gst');

  const executeBulkDelete = async () => {
    const targetHotelId = hotel?.id || null;
    setLoading(true);
    try {
      const monthParam = dateMode === 'month' ? selectedMonth : null;
      const startParam = dateMode === 'range' ? startDate : null;
      const endParam = dateMode === 'range' ? endDate : null;
      
      const res = await maintenanceApi.bulkDelete(targetHotelId, monthParam, startParam, endParam);
      onSuccess?.(`Successfully cleaned up ${res.count || nonGstRecords.length} Non-GST records.`);
      setRecords([]);
      checkCount();
    } catch (err) {
      await notify(err.message);
    }
    setLoading(false);
    setConfirmConfig(null);
  };

  const handleDelete = () => {
    const targetHotelId = hotel?.id || '';
    const scopeSet = targetHotelId ? `for ${hotel.name}` : 'for ALL hotels';
    const period = dateMode === 'month' ? selectedMonth : `from ${startDate} to ${endDate}`;
    
    setConfirmConfig({
      icon: '🗑️',
      title: 'Delete Non-GST Records?',
      message: `Are you sure you want to permanently delete all ${nonGstRecords.length} Non-GST records ${scopeSet} ${period}?`,
      detail: `The ${gstRecords.length} GST records will NOT be affected. This cannot be undone.`,
      confirmText: 'Delete All',
      type: 'danger',
      onConfirm: executeBulkDelete
    });
  };

  const executeSingleDelete = async (id) => {
    try {
      await reservationsApi.delete(id, true);
      onSuccess?.('Record deleted.');
      setViewingRecord(null);
      checkCount();
    } catch (err) { await notify(err.message); }
    setConfirmConfig(null);
  };

  // Shared with Reports → GST so both exports are identical.
  const toExportRows = () => gstRecords.map(r => {
    const total = Number(r.inv_total_amount ?? r.total_amount ?? 0);
    const totalGst = Number(r.inv_gst_amount ?? r.gst_amount ?? 0);
    const half = Math.round((totalGst / 2) * 100) / 100;
    return {
      invoiceNumber: r.invoice_number,
      date: r.check_in,
      guestName: r.guest_name,
      company: r.company,
      companyGst: r.company_gst || r.guest_company_gst,
      room: r.room,
      taxable: total - totalGst,
      cgst: half,
      sgst: half,
      totalGst,
      total,
    };
  });

  const handleDownload = async () => {
    if (gstRecords.length === 0) return await notify('No GST records to download.');
    const label = dateMode === 'month' ? `Month_${selectedMonth}` : `Period_${startDate}_to_${endDate}`;
    const title = `GST_Report_${label}`;
    
    const hotelSource = hotel ? (allHotelsRaw.find(h => String(h.id) === String(hotel.id)) || hotel) : null;
    const args = {
      rows: toExportRows(),
      hotelInfo: hotelSource ? hotelInfoFrom(hotelSource) : { name: 'All Properties — Consolidated' },
      periodLabel: dateMode === 'month' ? selectedMonth : `${toDDMMYYYY(startDate)} to ${toDDMMYYYY(endDate)}`,
      fileName: title,
    };
    try {
      if (downloadFormat === 'pdf') downloadGstPdf(args);
      else await downloadGstExcel(args);
    } catch (err) {
      await notify('Could not create the GST file: ' + err.message);
    }
  };

  return (
    <div className="panel" style={{ maxWidth: 1100, margin: '20px auto' }}>
      <div className="panel-head">
        <span className="panel-title">🛡️ Super Admin - Cross-Property Data Maintenance {hotel ? `(${hotel.name})` : '(All Properties)'}</span>
      </div>
      <div style={{ padding: 24 }}>
        <div style={{ background: 'var(--s1)', padding: 25, borderRadius: 16, border: '1px solid var(--border)', marginBottom: 25 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 1.5fr 1fr', gap: 30 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, opacity: 0.6, display: 'block', marginBottom: 10 }}>1. REPORT TYPE</label>
              <div style={{ display: 'flex', background: 'var(--bg)', padding: 4, borderRadius: 8, border: '1px solid var(--border)' }}>
                <button 
                  onClick={() => { setDateMode('month'); setRecords([]); }}
                  style={{ flex: 1, padding: '8px 12px', fontSize: 12, borderRadius: 6, border: 'none', background: dateMode === 'month' ? 'var(--gold)' : 'transparent', color: dateMode === 'month' ? 'white' : 'var(--text)', fontWeight: 700, cursor: 'pointer' }}
                >📅 Month Wise</button>
                <button 
                  onClick={() => { setDateMode('range'); setRecords([]); }}
                  style={{ flex: 1, padding: '8px 12px', fontSize: 12, borderRadius: 6, border: 'none', background: dateMode === 'range' ? 'var(--gold)' : 'transparent', color: dateMode === 'range' ? 'white' : 'var(--text)', fontWeight: 700, cursor: 'pointer' }}
                >↔️ Custom Range</button>
              </div>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, opacity: 0.6, display: 'block', marginBottom: 10 }}>2. SELECT PERIOD</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {dateMode === 'month' ? (
                  <input type="month" className="input" style={{ flex: 1, height: 42 }} value={selectedMonth} onChange={e => { setSelectedMonth(e.target.value); setRecords([]); }} />
                ) : (
                  <>
                    <input type="date" className="input" style={{ flex: 1, height: 42 }} value={startDate} onChange={e => { setStartDate(e.target.value); setRecords([]); }} />
                    <span style={{ opacity: 0.5 }}>to</span>
                    <input type="date" className="input" style={{ flex: 1, height: 42 }} value={endDate} onChange={e => { setEndDate(e.target.value); setRecords([]); }} />
                  </>
                )}
                <button className="btn btn-teal" style={{ minWidth: 120, height: 42 }} onClick={checkCount} disabled={loading}>
                  {loading ? '⌛ ...' : '🔍 Scan'}
                </button>
              </div>
            </div>

            <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: 30 }}>
              <label style={{ fontSize: 11, fontWeight: 700, opacity: 0.6, display: 'block', marginBottom: 10 }}>3. EXPORT SETTINGS</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <select 
                  className="input" 
                  style={{ flex: 1.2, height: 42, fontSize: 12, fontWeight: 600 }}
                  value={downloadFormat}
                  onChange={e => setDownloadFormat(e.target.value)}
                >
                  <option value="excel">📊 Excel (.xlsx)</option>
                  <option value="pdf">📄 PDF Report</option>
                </select>
                <button className="btn btn-gold" style={{ flex: 1, height: 42 }} onClick={handleDownload} disabled={gstRecords.length === 0}>
                  📥 Get
                </button>
              </div>
            </div>
          </div>
          
          {nonGstRecords.length > 0 && (
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px dashed var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--danger)' }}>
                ⚠️ <strong>Security Notice:</strong> Found {nonGstRecords.length} unofficial records in this period.
              </div>
              <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={loading}>
                🗑️ Bulk Clean Non-GST Data
              </button>
            </div>
          )}
        </div>

        {records.length > 0 && (
          <div style={{ marginTop: 25 }}>
            <div style={{ display: 'flex', gap: 15, marginBottom: 15, fontSize: 12 }}>
               <div style={{ background: 'var(--bg)', padding: '8px 16px', borderRadius: 8, border: '2px solid var(--gold)', color: 'var(--gold)', fontWeight: 700 }}>
                 {gstRecords.length} Official GST Records
               </div>
               <div style={{ background: 'var(--bg)', padding: '8px 16px', borderRadius: 8, border: '2px solid var(--danger)', color: 'var(--danger)', fontWeight: 700 }}>
                 {nonGstRecords.length} Non-GST Records
               </div>
            </div>

            <div className="tbl-wrap" style={{ border: '1px solid var(--border)', borderRadius: 12, maxHeight: 600, overflowY: 'auto' }}>
              <table style={{ fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--s1)', position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={{ width: 100 }}>Date</th>
                    <th>Guest Name</th>
                    <th>Company</th>
                    <th>GSTIN</th>
                    <th>Invoice</th>
                    <th style={{ textAlign: 'right' }}>Taxable</th>
                    <th style={{ textAlign: 'right' }}>Tax (5%)</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => {
                    const isGst = r.billing_type === 'gst';
                    const taxAmt = Number(r.inv_gst_amount ?? r.gst_amount ?? 0);
                    const totAmt = Number(r.inv_total_amount ?? r.total_amount ?? 0);
                    const taxable = Math.max(0, totAmt - taxAmt);
                    const compName = r.company || r.guest_company || 'Individual';
                    const gstinNum = r.company_gst || r.guest_company_gst || 'N/A';

                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s', background: isGst ? 'transparent' : 'rgba(255,0,0,0.01)' }}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{r.check_in}</td>
                        <td style={{ fontWeight: 700, color: 'var(--text)' }}>
                          <div>{r.guest_name}</div>
                          <div style={{ fontSize: 9, opacity: 0.5 }}>{hotelMap[r.hotel_id]?.displayName}</div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{compName}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11, opacity: 0.8 }}>{gstinNum}</td>
                        <td style={{ fontFamily: 'monospace', color: 'var(--muted)', fontSize: 11 }}>{r.invoice_number || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--muted)' }}>{fmt(taxable)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--muted)' }}>{fmt(taxAmt)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: isGst ? 'var(--gold)' : 'var(--teal)' }}>{fmt(totAmt)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button className="btn btn-sm btn-ghost" style={{ padding: '2px 8px' }} onClick={() => setViewingRecord(r)}>👁️</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {records.length === 0 && !loading && selectedMonth && (
          <div style={{ marginTop: 25, textAlign: 'center', padding: 80, background: 'var(--s1)', borderRadius: 16, border: '1px dashed var(--border)' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>📊</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>No scanning performed yet</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>Choose a month above and click <strong>Scan Records</strong> to view cross-property data.</div>
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      {viewingRecord && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 500 }}>
            <div className="modal-head">
              <h3>Reservation Detail</h3>
              <button className="close-btn" onClick={() => setViewingRecord(null)}>×</button>
            </div>
            <div style={{ padding: 24 }}>
               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px 20px', marginBottom: 20 }}>
                 <div><label style={{ fontSize: 10, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>GUEST</label><div style={{ fontWeight: 700 }}>{viewingRecord.guest_name}</div></div>
                 <div><label style={{ fontSize: 10, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>PHONE</label><div style={{ fontWeight: 700 }}>{viewingRecord.guest_phone || '—'}</div></div>
                 <div><label style={{ fontSize: 10, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>HOTEL</label><div style={{ fontWeight: 700 }}>{hotelMap[viewingRecord.hotel_id]?.displayName || '—'}</div></div>
                 <div><label style={{ fontSize: 10, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>BILLING TYPE</label>
                   <div style={{ fontWeight: 700, color: viewingRecord.billing_type === 'gst' ? 'var(--gold)' : 'var(--muted)' }}>
                     {viewingRecord.billing_type?.toUpperCase() || 'NON-GST'}
                   </div>
                 </div>
                 <div><label style={{ fontSize: 10, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>CHECK IN</label><div style={{ fontWeight: 700 }}>{viewingRecord.check_in}</div></div>
                 <div><label style={{ fontSize: 10, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>CHECK OUT</label><div style={{ fontWeight: 700 }}>{viewingRecord.check_out}</div></div>
                 <div style={{ gridColumn: '1/-1', padding: '12px 16px', background: 'var(--s1)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div><label style={{ fontSize: 10, color: 'var(--muted)' }}>TOTAL AMOUNT</label><div style={{ fontWeight: 800, fontSize: 18 }}>{fmt(viewingRecord.total_amount)}</div></div>
                    <div style={{ textAlign: 'right' }}><label style={{ fontSize: 10, color: 'var(--muted)' }}>INVOICE</label><div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{viewingRecord.invoice_number || 'None'}</div></div>
                 </div>
               </div>
               
               {(!viewingRecord.billing_type || viewingRecord.billing_type === 'non-gst') && (
                 <div style={{ background: 'rgba(255,0,0,0.05)', padding: 16, borderRadius: 10, border: '1px solid rgba(255,0,0,0.1)', marginTop: 10 }}>
                   <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>This is an unofficial record and can be deleted to clean up database growth.</div>
                   <button className="btn btn-danger btn-sm" style={{ width: '100%', height: 36 }} onClick={() => {
                     setConfirmConfig({
                       icon: '🗑️',
                       title: 'Delete Single Record?',
                       message: 'Are you sure you want to permanently delete this reservation?',
                       detail: 'This will remove all associated payment history. This action cannot be undone.',
                       confirmText: 'Yes, Delete',
                       type: 'danger',
                       onConfirm: () => executeSingleDelete(viewingRecord.id)
                     });
                   }}>🗑️ Delete Single Record</button>
                 </div>
               )}
               {viewingRecord.billing_type === 'gst' && (
                 <div style={{ background: 'rgba(200,150,0,0.05)', padding: 16, borderRadius: 10, border: '1px solid rgba(200,150,0,0.1)', marginTop: 10 }}>
                   <div style={{ fontSize: 12, color: 'var(--gold)' }}>🛡️ This is an official GST record and <strong>cannot be deleted</strong> to maintain audit integrity.</div>
                 </div>
               )}
            </div>
          </div>
        </div>
      )}

      {/* DESIGNER CONFIRM POPUP (MODERN) */}
      {confirmConfig && (
        <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 3000 }}>
          <div className="modal-content" style={{ 
            maxWidth: 400, 
            borderRadius: 28, 
            padding: '40px 32px', 
            textAlign: 'center', 
            boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
            border: 'none',
            animation: 'modalSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}>
            <div style={{ fontSize: 56, marginBottom: 24, filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.1))' }}>{confirmConfig.icon}</div>
            <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 16, color: 'var(--text)', letterSpacing: '-0.3px' }}>{confirmConfig.title}</h2>
            <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 24, padding: '0 10px' }}>{confirmConfig.message}</p>
            
            {confirmConfig.detail && (
              <div style={{ 
                fontSize: 12, 
                color: 'var(--danger)', 
                fontWeight: 600, 
                padding: '12px 16px', 
                background: 'rgba(255,0,0,0.05)', 
                borderRadius: 16, 
                marginBottom: 32,
                border: '1px dashed rgba(255,0,0,0.2)'
              }}>
                {confirmConfig.detail}
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <button 
                className="btn btn-ghost" 
                style={{ borderRadius: 16, height: 54, fontWeight: 700, fontSize: 16, border: '1.5px solid var(--border)', background: 'white' }}
                onClick={() => setConfirmConfig(null)}
              >
                Cancel
              </button>
              <button 
                className={`btn btn-${confirmConfig.type || 'teal'}`} 
                style={{ 
                  borderRadius: 16, 
                  height: 54, 
                  fontWeight: 800, 
                  fontSize: 16,
                  background: confirmConfig.type === 'danger' ? 'var(--danger)' : 'var(--success)',
                  color: 'white',
                  boxShadow: `0 10px 20px ${confirmConfig.type === 'danger' ? 'rgba(231, 76, 60, 0.3)' : 'rgba(46, 204, 113, 0.3)'}` 
                }}
                onClick={confirmConfig.onConfirm}
              >
                {confirmConfig.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
